import { IColumn, Separator } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPScrollablePane, IPShimmeredDetailsList, IPSpinner, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import { KeyValueTag } from 'components/Tags/KeyValueTag';
import { getEntityTypeName, onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalStackItemStylesPaddingSceneScroll, globalStackTokensGapSmall } from 'globalStyles';
import Entity from 'models/entity';
import Tag from 'models/tag';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetTagUsage } from 'services/Api/tagService';
import { apiRequest } from 'services/Auth/authConfig';

interface ITagUsageModal {
  tag?: Tag;
  dismiss: () => void;
}

export const TagUsageModal = (props: ITagUsageModal) => {
  const appContext = useContext(AppContext);

  const [tagUsage, setTagUsage] = useState<Entity[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation(['tags', 'translation']);

  useEffect(() => {
    const loadData = async (tag: Tag) => {
      try {
        setIsLoading(true);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const tagUsage = await apiGetTagUsage(tag.tagId, accessToken);

        setTagUsage(tagUsage);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (props.tag) {
      loadData(props.tag);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tag]);

  const columns: IColumn[] = [
    {
      key: 'type',
      name: t('tags:UsageModal.Columns.Type'),
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      onRender: (item?: Entity, index?: number, column?: IColumn) => {
        if (!item?.typeOfEntity) return;

        return <IPText>{getEntityTypeName(item?.typeOfEntity, t)}</IPText>;
      },
    },
    {
      key: 'name',
      name: t('tags:UsageModal.Columns.Name'),
      minWidth: 300,
      isResizable: true,
      onRender: (item?: Entity, index?: number, column?: IColumn) => {
        return (
          <IPText>
            <HrefLink entity={item} external includeContext>
              {item?.entityName ? item?.entityName : t('tags:UsageModal.NameUnknown')}
            </HrefLink>
          </IPText>
        );
      },
    },
  ];

  return (
    <GenericModal
      onClose={props.dismiss}
      isOpen={props.tag !== undefined}
      title={t('tags:UsageModal.Title')}
      height={'70vh'}
      width={700}
      maxWidth={'min(90vw, 700px)'}
      maxHeight={'95vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <Separator />
        </IPStack.Item>
        <IPStack verticalFill tokens={globalStackTokensGapSmall}>
          <IPStack.Item>{props.tag && <KeyValueTag tag={props.tag} />}</IPStack.Item>
          <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
            {isLoading && (
              <IPStack horizontalAlign="center" verticalAlign="center">
                <IPStack.Item>
                  <IPSpinner size={IPSpinnerSize.large} />
                </IPStack.Item>
              </IPStack>
            )}
            {tagUsage && (
              <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
                <IPShimmeredDetailsList
                  compact
                  enableShimmer={isLoading}
                  columns={columns}
                  items={tagUsage as Entity[]}
                  shimmerLines={3}
                  selectionMode={IPSelectionMode.none}
                  layoutMode={IPDetailsListLayoutMode.justified}
                  onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
                />
              </IPScrollablePane>
            )}
          </IPStack.Item>
        </IPStack>
      </IPStack>
    </GenericModal>
  );
};
